/*!
 * NUO v1.0.0
 * (c) 2016 crossjs
 * Released under the MIT License.
 */
'use strict';

var setImmediate = require('core-js/library/fn/set-immediate');

function Nuo (fn) {
  var this$1 = this;

  if (typeof this !== 'object') { throw new TypeError('Promises must be constructed via new') }
  if (typeof fn !== 'function') { throw new TypeError('The first argument must be a function') }
  this._s = null;
  this._v = null;
  this._p = null;
  this._d = [];

  doResolve(fn, function () {
    var args = [], len = arguments.length;
    while ( len-- ) args[ len ] = arguments[ len ];

    resolve.apply(this$1, args);
  }, function () {
    var args = [], len = arguments.length;
    while ( len-- ) args[ len ] = arguments[ len ];

    reject.apply(this$1, args);
  }, function () {
    var args = [], len = arguments.length;
    while ( len-- ) args[ len ] = arguments[ len ];

    notify.apply(this$1, args);
  });
}

function handle (deferred) {
  var this$1 = this;

  if (this._s === null) {
    this._d.push(deferred);
    if (deferred.onProgress) {
      if (this._p !== null) {
        setImmediate(function () {
          deferred.onProgress(this$1._p);
        });
      }
    }
    return
  }

  setImmediate(function () {
    var cb = this$1._s ? deferred.onFulfilled : deferred.onRejected;
    if (cb === null) {
      (this$1._s ? deferred.resolve : deferred.reject)(this$1._v);
      return
    }
    var ret;
    try {
      ret = cb(this$1._v);
    } catch (e) {
      deferred.reject(e);
      return
    }
    deferred.resolve(ret);
  });
}

function resolve (newValue) {
  var this$1 = this;

  try {
    // Promise Resolution Procedure: https://github.com/promises-aplus/promises-spec#the-promise-resolution-procedure
    if (newValue === this) {
      throw new TypeError('A promise cannot be resolved with itself.')
    }

    if (newValue && (typeof newValue === 'object' || typeof newValue === 'function')) {
      var then = newValue.then;
      if (typeof then === 'function') {
        doResolve(function () {
          var args = [], len = arguments.length;
          while ( len-- ) args[ len ] = arguments[ len ];

          then.apply(newValue, args);
        }, function () {
          var args = [], len = arguments.length;
          while ( len-- ) args[ len ] = arguments[ len ];

          resolve.apply(this$1, args);
        }, function () {
          var args = [], len = arguments.length;
          while ( len-- ) args[ len ] = arguments[ len ];

          reject.apply(this$1, args);
        }, function () {
          var args = [], len = arguments.length;
          while ( len-- ) args[ len ] = arguments[ len ];

          notify.apply(this$1, args);
        });
        return
      }
    }

    this._s = true;
    this._v = newValue;
    finale.call(this);
  } catch (e) {
    reject.call(this, e);
  }
}

function reject (newValue) {
  this._s = false;
  this._v = newValue;
  finale.call(this);
}

function notify (progress) {
  this._p = progress;
  finale.call(this, true);
}

function finale (keep) {
  var this$1 = this;

  for (var i = 0, len = this._d.length; i < len; i++) {
    handle.call(this$1, this$1._d[i]);
  }
  if (!keep) {
    this._d = null;
  }
}

function Handler (onFulfilled, onRejected, onProgress, resolve, reject, notify) {
  this.onFulfilled = typeof onFulfilled === 'function' ? onFulfilled : null;
  this.onRejected = typeof onRejected === 'function' ? onRejected : null;
  this.onProgress = typeof onProgress === 'function' ? onProgress : null;
  this.resolve = resolve;
  this.reject = reject;
  this.notify = notify;
}

/**
 * Take a potentially misbehaving resolver function and make sure
 * onFulfilled and onRejected are only called once.
 *
 * Makes no guarantees about asynchrony.
 */
function doResolve (fn, onFulfilled, onRejected, onProgress) {
  var done = false;
  try {
    fn(function (value) {
      if (done) { return }
      done = true;
      onFulfilled(value);
    }, function (reason) {
      if (done) { return }
      done = true;
      onRejected(reason);
    }, function (progress) {
      if (done) { return }
      onProgress(progress);
    });
  } catch (e) {
    if (done) { return }
    done = true;
    onRejected(e);
  }
}

Nuo.prototype.catch = function (onRejected) {
  return this.then(null, onRejected)
};

Nuo.prototype.finally = function (done) {
  return this.then(function (value) { return Nuo.resolve(done()).then(function () {
    return value
  }); }, function (reason) { return Nuo.resolve(done()).then(function () {
    throw reason
  }); })
};

Nuo.prototype.progress = function (onProgress) {
  return this.then(null, null, onProgress)
};

Nuo.prototype.then = function (onFulfilled, onRejected, onProgress) {
  var this$1 = this;

  return new Nuo(function (resolve, reject, notify) {
    handle.call(this$1, new Handler(onFulfilled, onRejected, onProgress, resolve, reject, notify));
  })
};

Nuo.resolve = function (value) {
  if (value && typeof value === 'object' && value.constructor === Nuo) {
    return value
  }

  return new Nuo(function (resolve) { return resolve(value); })
};

Nuo.reject = function (value) { return new Nuo(function (resolve, reject) { return reject(value); }); };

Nuo.notify = function (value) { return new Nuo(function (resolve, reject, notify) { return notify(value); }); };

Nuo.all = function (values) {
  return new Nuo(function (resolve, reject, notify) {
    if (values.length === 0) { return resolve([]) }
    var remaining = values.length;

    function res (i, val) {
      try {
        if (val && (typeof val === 'object' || typeof val === 'function')) {
          var then = val.then;
          if (typeof then === 'function') {
            then.call(val, function (val) {
              res(i, val);
            }, reject, notify);
            return
          }
        }
        values[i] = val;
        if (--remaining === 0) {
          resolve(values);
        }
      } catch (e) {
        reject(e);
      }
    }
    for (var i = 0; i < values.length; i++) {
      res(i, values[i]);
    }
  })
};

Nuo.race = function (values) {
  return new Nuo(function (resolve, reject, notify) {
    for (var i = 0, len = values.length; i < len; i++) {
      Nuo.resolve(values[i]).then(resolve, reject, notify);
    }
  })
};

module.exports = Nuo;



//////////////////
// WEBPACK FOOTER
// ./~/nuo/dist/index.js
// module id = 173
// module chunks = 9